// Редактор списка кнопок блока. Кнопки локализованы: массив блока держит и ru, и en,
// форма правит только текущий язык контента, чужой переезжает нетронутым.
// Отдельных ручек кнопок нет — список уезжает вместе с блоком в PUT (контракт §TL;DR п.1).
import { Plus } from 'lucide-react'
import { Button, Label } from '@teach/ui'
import type { BlockButton, BlockType } from '@/types/blocks'
import {
  buttonLimitFor,
  buttonsForLang,
  mergeButtonsForLang,
  newLinkButton,
} from '@/features/editor/lib/blockButtons'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useStableKeys } from '@/features/editor/hooks/useStableKeys'
import { BlockButtonRow } from './BlockButtonRow'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  blockType: BlockType
  buttons: BlockButton[] | undefined
  onChange: (buttons: BlockButton[]) => void
}

export const BlockButtonsEditor = ({ blockType, buttons, onChange }: Props) => {
  const m = useMessages(messages)
  const lang = useContentLang()
  const limit = buttonLimitFor(blockType)
  const items = buttonsForLang(buttons, lang)
  const canAdd = items.length < limit
  // Ключ строки — не `id` кнопки: бек пересоздаёт кнопки на каждом сохранении и раздаёт
  // новые id (§5), а `key={index}` ломает фокус при удалении из середины.
  const { keys, removeAt } = useStableKeys(items.length, `button-${lang}`)

  const commit = (next: BlockButton[]) => onChange(mergeButtonsForLang(buttons, lang, next))

  const patchAt = (index: number, patch: Partial<BlockButton>) =>
    commit(items.map((b, i) => (i === index ? { ...b, ...patch } : b)))

  const removeButton = (index: number) => {
    removeAt(index)
    commit(items.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        {/* Язык — в шапке секции, а не отдельной подсказкой снизу: подсказка
            «Отображается на языке …» в этой же панели уже есть у изображения. */}
        <div className="flex items-center gap-2">
          <Label className="text-foreground font-medium">{m.buttons}</Label>
          <span className="text-xs text-muted-foreground">
            · {items.length}/{limit} · {lang.toUpperCase()}
          </span>
        </div>
        {canAdd ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => commit([...items, newLinkButton(lang)])}
            className="h-6 px-2 text-xs"
          >
            <Plus className="mr-1 h-3 w-3" />
            {m.add}
          </Button>
        ) : null}
      </div>

      {items.length ? (
        <div className="space-y-2">
          {items.map((button, index) => (
            <BlockButtonRow
              key={keys[index]}
              button={button}
              onChange={(patch) => patchAt(index, patch)}
              onRemove={() => removeButton(index)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{m.buttonsEmpty}</p>
      )}
    </div>
  )
}
